import { ChatRoom, ChatRoomType } from '@dinosband/bv-main-schema/dist/types'
import { _isEqualArray } from '@dinosband/dbi-utils'

import { BvSubscriber, GroupChatRoomSubscriber, PrivateChatRoomSubscriber } from '../../subscriber'
import { DataCenter_StoreType } from '../abstract'
import { CHAT_ROOM_ADDED, CHAT_ROOM_REMOVED } from './const'
import { CreatePrivateChatRoomArgs } from '@dinosband/bv-main-client'

export class PrivateChatRooms extends DataCenter_StoreType<number, ChatRoom> {

  private _subscribers: BvSubscriber<ChatRoom> [] = []

  async init () {
    const privateSub = new PrivateChatRoomSubscriber(this.context, (value) => this.onChatRoomAdded(value))
    const groupSubs = new GroupChatRoomSubscriber(this.context, (value) => this.onChatRoomUpdated(value))
    this._subscribers = [privateSub, groupSubs]
    await Promise.all(this._subscribers.map((i) => i.start()))
  }

  cleanup () {
    super.cleanup()
    this._subscribers.forEach((i) => i.cleanup())
    this._subscribers = []
  }

  get chatRooms () {
    return this.values
  }

  onReconnected () {
    this._subscribers.forEach((i) => i.retrieve())
  }

  findChatRoom (profiles: number[], rootMessageId?: number) {
    const profileIds = profiles.map((i) => i).sort()
    return this.chatRooms
      .filter((i) => i.type === ChatRoomType.ONE_TO_ONE)
      .filter((i) => i.rootMessageId == rootMessageId) // null == undefined: true
      .find((chatRoom) => _isEqualArray((chatRoom.participants ?? []).map((i) => i.profileId).sort(), profileIds))
  }

  findChatRooms (profiles: number[], rootMessageId?: number) {
    const profileIds = profiles.map((i) => i).sort()
    return this.chatRooms
      .filter((i) => i.type === ChatRoomType.ONE_TO_ONE)
      .filter((i) => i.rootMessageId == rootMessageId) // null == undefined: true
      .filter((chatRoom) => _isEqualArray((chatRoom.participants ?? []).map((i) => i.profileId).sort(), profileIds))
  }

  async findGroupChatRoom (rootMessageId: number) {
    const found = this.chatRooms.find((i) => i.rootMessageId == rootMessageId && i.type === ChatRoomType.JOB_GROUP)
    if (found) {
      return found
    }
    const userId = this.mainStore.userId
    return await this.mainStore.readGroupChatRoom(rootMessageId, userId)
  }

  async requestPrivateChatRoom (args: CreatePrivateChatRoomArgs) {
    if (!args.forceCreate) {
      const profileIds = [args.targetProfileId, args.requesterProfileId]
      const selected = this.findChatRoom(profileIds, args.rootMessageId)
      if (selected) {
        return selected
      }
    }
    const chatRoom = await this.mainStore.createPrivateChatRoom(args)
    if (chatRoom) {
      this.onChatRoomAdded(chatRoom)
    }
    return chatRoom
  }

  async requestGroupChatRoom (args: CreatePrivateChatRoomArgs) {
    const { jobOffer, jrsp, channelId } = args
    const rootMessageId = jobOffer?.id ?? jrsp?.jobOfferId
    if (!rootMessageId || !channelId) {
      return
    }
    let chatRoom = await this.findGroupChatRoom(rootMessageId)
    if (chatRoom) {
      return chatRoom
    }
    chatRoom = await this.mainStore.createGroupChatRoom(rootMessageId, channelId)
    if (chatRoom) {
      this.onChatRoomAdded(chatRoom)
    }
    return chatRoom
  }

  private onChatRoomAdded (value: ChatRoom | ChatRoom[]) {
    const chatRooms = value instanceof Array ? value : [value]
    chatRooms.forEach((i) => this.set(i.id, i))
    this.pubsub.publish(CHAT_ROOM_ADDED, {
      type: ChatRoomType.ONE_TO_ONE,
      chatRooms,
    })
  }

  private onChatRoomUpdated (value: ChatRoom | ChatRoom[]) {
    const chatRooms = value instanceof Array ? value : [value]
    for (const chatRoom of chatRooms) {
      const joined = chatRoom.participants.some((i) => i.userId === this.mainStore.userId)
      if (!joined) {
        this.remove(chatRoom.id)
      }
      const event = joined ? CHAT_ROOM_ADDED : CHAT_ROOM_REMOVED
      this.pubsub.publish(event, {
        type: chatRoom.type,
        chatRooms: [chatRoom],
      })
    }
  }

}
